import { useEffect, useMemo } from 'react'
import { useParams, generatePath, useNavigate } from 'react-router-dom'

import Page from 'commons/page'
import Block from 'components/block'
import ButtonV2 from 'components/button-v2'
import { SectionTitle } from 'components/typography'
import { useAuthProvider } from 'contexts/auth'
import { useAppDispatch, useAppSelector } from 'hooks'
import AccessDenied from 'pages/access-denied'
import {
  fetchContractById,
  selectContractById,
  selectContractByIdFailed,
  selectContractByIdLoading,
} from 'stores/contract'

import styled from 'styled-components'
import { UserRole } from '../../schema/user'
import { SuccessfullyConcludedDealItem } from '../../schema/contract'
import SubPageHeader from '../../components/sub-page-header'
import SuccessfullyConcludedDealList from '../../features/contracts/list/successfully-concluded-deal-list'
import { Horizontal } from '../../components/layout'
import ROUTING_PATH from '../../constants/route'

const SectionHeader = styled(Horizontal)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`

const SuccessfullyConcludedDealsListWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;
  justify-items: center;

  @media only screen and (min-width: ${(p) => p.theme.vp}px) {
    grid-template-columns: repeat(3, 1fr);
    justify-items: stretch;
  }
`
const SuccessfullyConcludedDeals = () => {
  const { claims } = useAuthProvider()
  const contract = useAppSelector(selectContractById)
  const contractLoading = useAppSelector(selectContractByIdLoading)
  const contractFailed = useAppSelector(selectContractByIdFailed)
  const dispatch = useAppDispatch()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  useEffect(() => {
    if (!claims?.role) {
      return
    }

    if (!id) {
      return
    }

    dispatch(fetchContractById({ role: claims?.role, id }))
  }, [dispatch, claims?.role, id])

  const data = useMemo(() => {
    return contract
  }, [contract])

  const HasApiError = useMemo(() => {
    return contractFailed
  }, [contractFailed])

  const assignNumbers = (items: SuccessfullyConcludedDealItem[]) => {
    let currentItemNumber = 1
    return items.map((item) => {
      const isLocated = item.lat && item.lon
      const itemNumberLabel = isLocated ? String(currentItemNumber++) : '-'
      return {
        ...item,
        itemNumberLabel,
      }
    })
  }

  const { successfullyConcludedDeals } = useMemo(() => {
    if (!data) {
      return {
        successfullyConcludedDeals: [],
      }
    }
    const numberedSuccessfullyConcludedDeals = assignNumbers(
      data.successfullyConcludedDeal?.items ?? []
    )

    return {
      successfullyConcludedDeals: numberedSuccessfullyConcludedDeals,
    }
  }, [data])

  const handleMapButtonClick = () => {
    const mapPath = generatePath(
      claims?.role === UserRole.OWNER
        ? ROUTING_PATH.OwnerSuccessfullyConcludedDealsMap
        : ROUTING_PATH.AgentSuccessfullyConcludedDealsMap,
      { id: id! }
    )
    navigate(mapPath)
  }

  return (
    <>
      {HasApiError && <AccessDenied />}
      {!HasApiError && (
        <Page
          title={`周辺の成約事例`}
          loading={contractLoading}
          type="user"
          customHeader={<SubPageHeader />}
        >
          {successfullyConcludedDeals.length !== 0 && (
            <Block>
              <SectionHeader>
                <SectionTitle>周辺の成約事例</SectionTitle>
                <ButtonV2
                  variant="secondary"
                  size="sm"
                  onClick={handleMapButtonClick}
                >
                  <img src="/image/map.svg" alt="地図" width="16" height="16" />
                  地図を見る
                </ButtonV2>
              </SectionHeader>
              <SuccessfullyConcludedDealsListWrapper>
                <SuccessfullyConcludedDealList
                  list={successfullyConcludedDeals}
                  isVertical={true}
                  background="white"
                />
              </SuccessfullyConcludedDealsListWrapper>
            </Block>
          )}
        </Page>
      )}
    </>
  )
}

export default SuccessfullyConcludedDeals
